import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { i18nGlobal } from '../i18n'
import type { User } from '../types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Rol binnen het tenant-account (owner/member, SAAS_PLAN.md §2) —
  // alleen voor UI-gating (facturering-menu e.d.); de backend dwingt
  // owner-only-acties los hiervan af.
  const accountRole = ref<'owner' | 'member' | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.roles?.includes('admin') ?? false)
  const isEditor = computed(() => {
    const roles = user.value?.roles || []
    return roles.includes('admin') || roles.includes('editor')
  })
  const isAccountOwner = computed(() => accountRole.value === 'owner')

  /**
   * Sla token + gebruiker op deze origin op en zet de Authorization-header
   * voor volgende requests. Gedeeld door de tenant-login en de hand-off:
   * beide leveren dezelfde payload (token/user/account_role).
   */
  function applySession(data: { token: string; user: User; accountRole?: 'owner' | 'member' | null }) {
    token.value = data.token
    user.value = data.user
    accountRole.value = data.accountRole ?? null
    localStorage.setItem('auth_token', data.token)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`
  }

  /**
   * Plak de hand-off-code achter het adres van de eigen omgeving. Dat adres
   * komt van de server (handoff.url), want de browser kan het niet betrouwbaar
   * afleiden: draait de app op een eigen adres (app.pentrical.com), dan zou
   * "{subdomein} + huidige host" uitkomen op klant.app.pentrical.com.
   */
  function buildHandoffUrl(baseUrl: string, code: string): string {
    return `${baseUrl.replace(/\/$/, '')}/auth/handoff?code=${encodeURIComponent(code)}`
  }

  // Eén omgeving waar deze gebruiker lid van is; het adres komt van de
  // server (zie buildHandoffUrl).
  type Environment = { subdomain: string; name: string; role: string; url: string }

  // Uitkomst van login(): tenant-login geeft direct een sessie; centrale
  // (platform)login stuurt door naar de eigen omgeving, laat kiezen bij meer
  // dan één, of meldt dat er geen bereikbare omgeving is.
  type LoginOutcome =
    | { status: 'authenticated' }
    | { status: 'redirecting'; environment: string }
    | { status: 'choose'; environments: Environment[]; code: string }
    | { status: 'noEnvironments' }
    | { status: 'error' }

  /** Naar de gekozen omgeving; volledige navigatie, dus geen return. */
  function goToEnvironment(environment: Environment, code: string): void {
    window.location.assign(buildHandoffUrl(environment.url, code))
  }

  // Actions
  async function login(email: string, password: string): Promise<LoginOutcome> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/login', { email, password })
      const data = response.data

      // Centrale (platform)login: geen token op dit domein, maar een
      // eenmalige hand-off naar de eigen omgeving. Zie AuthController.
      if (data.central) {
        if (!data.handoff) {
          // Geldige inlog, maar (nog) geen bereikbare omgeving (subdomein).
          return { status: 'noEnvironments' }
        }

        const environments: Environment[] = data.environments ?? []

        // Meer dan één omgeving: laten kiezen in plaats van gokken. De
        // code hoort bij de gebruiker en werkt voor elke omgeving waar hij
        // lid van is, dus één code volstaat voor de hele keuze.
        if (environments.length > 1) {
          return { status: 'choose', environments, code: data.handoff.code }
        }

        // Volledige navigatie naar het tenant-subdomein: daar wisselt de
        // HandoffView de code in voor een echte sessie op die origin.
        window.location.assign(buildHandoffUrl(data.handoff.url, data.handoff.code))
        return { status: 'redirecting', environment: data.handoff.name }
      }

      // Normale (tenant)login: token hoort op deze origin.
      applySession(data)
      return { status: 'authenticated' }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18nGlobal.t('auth.login.failed')
      return { status: 'error' }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Wissel een hand-off code (uit de centrale login) in voor een sessie op
   * deze omgeving. Draait op het tenant-subdomein: de code is single-use en
   * kort geldig. Bij een verlopen/ongeldige code komt de melding uit de
   * backend (422), die we teruggeven zodat de HandoffView die kan tonen.
   */
  async function completeHandoff(code: string): Promise<{ ok: boolean; message?: string }> {
    error.value = null
    try {
      const response = await api.post('/auth/handoff', { code })
      applySession(response.data)
      return { ok: true }
    } catch (err: any) {
      const errors = err.response?.data?.errors as Record<string, string[]> | undefined
      const message =
        errors?.code?.[0] ||
        err.response?.data?.message ||
        i18nGlobal.t('auth.handoff.failed')
      return { ok: false, message }
    }
  }

  /**
   * Vraag een wachtwoord-resetlink aan. De backend antwoordt altijd
   * hetzelfde (of het adres nu bestaat of niet), dus succes betekent hier
   * enkel "verzoek verstuurd". Retourneert false alleen bij een echte fout
   * (bijv. rate limit), zodat de UI dat apart kan tonen.
   */
  async function forgotPassword(email: string): Promise<{ ok: boolean; rateLimited?: boolean }> {
    error.value = null
    try {
      await api.post('/auth/forgot-password', { email })
      return { ok: true }
    } catch (err: any) {
      if (err.response?.status === 429) return { ok: false, rateLimited: true }
      error.value = err.response?.data?.message || i18nGlobal.t('auth.forgot.failed')
      return { ok: false }
    }
  }

  /**
   * Zet een nieuw wachtwoord op basis van het token uit de resetmail.
   * Bij een ongeldig/verlopen token of validatiefout komt de melding uit
   * de backend (422); die geeft de aanroeper terug om te tonen.
   */
  async function resetPassword(payload: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }): Promise<{ ok: boolean; message?: string }> {
    error.value = null
    try {
      await api.post('/auth/reset-password', payload)
      return { ok: true }
    } catch (err: any) {
      const errors = err.response?.data?.errors as Record<string, string[]> | undefined
      const message =
        errors?.email?.[0] ||
        errors?.password?.[0] ||
        err.response?.data?.message ||
        i18nGlobal.t('auth.reset.failed')
      return { ok: false, message }
    }
  }

  /**
   * Wis alleen de client-side sessie (token, gebruiker, header), zonder de
   * server aan te roepen. Nodig na een wachtwoordreset: die trekt álle
   * tokens server-side in, dus een lokaal bewaard token is daarna dood.
   * `logout()` zou hier juist een 401 uitlokken (dood token → de
   * response-interceptor stuurt hard naar /login) — vandaar deze variant.
   */
  function clearClientAuth() {
    token.value = null
    user.value = null
    accountRole.value = null
    error.value = null
    localStorage.removeItem('auth_token')
    delete api.defaults.headers.common['Authorization']
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore errors on logout
    }

    clearClientAuth()
  }

  async function fetchUser() {
    if (!token.value) return null
    
    isLoading.value = true
    try {
      // Set token for request
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      
      const response = await api.get('/auth/user')
      // API returns { user: {...} }
      user.value = response.data.user || response.data.data
      accountRole.value = response.data.accountRole ?? null
      return user.value
    } catch (err: unknown) {
      /*
       * Alleen een écht afgewezen token is reden om de sessie te wissen.
       *
       * Hier stond `catch { logout() }`: élke fout gooide je eruit. Een 429
       * (te veel verzoeken - een paar keer herladen haalt de limiet al),
       * een serverfout of een netwerkhapering kostte je zo je sessie voor
       * een probleem dat vanzelf overgaat. Je moest daarna opnieuw
       * inloggen, terwijl je token nog prima was.
       *
       * Nu: 401 betekent dat het token niet meer geldt, en alleen dán
       * ruimen we op. Al het andere laat de sessie staan; de aanroeper
       * krijgt null en de volgende navigatie probeert het opnieuw.
       */
      const status = (err as { response?: { status?: number } })?.response?.status

      if (status === 401) {
        await logout()
      }

      return null
    } finally {
      isLoading.value = false
    }
  }

  // Initialize - check for existing token
  async function initialize() {
    if (token.value) {
      await fetchUser()
    }
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    accountRole,

    // Getters
    isAuthenticated,
    isAdmin,
    isEditor,
    isAccountOwner,
    
    // Actions
    login,
    goToEnvironment,
    completeHandoff,
    forgotPassword,
    resetPassword,
    clearClientAuth,
    logout,
    fetchUser,
    initialize,
  }
})
